package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initKompotPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("kompot.md", FrontMatterElement.Builder { addScalar("title", "kompot"); addScalar("searchTitle", "Backend-driven UI toolkit for Kotlin Multiplatform — server-driven screens"); addScalar("description", "The server describes a screen as a tree of components, the client renders it, and a new screen ships without a client release. Open hierarchies, KSP registration, forms, theming, and two conformance kits."); addScalar("library", "kompot"); addScalar("post", "/blog/backend-driven-ui-for-kotlin-multiplatform"); }))
}

@Page("/kompot")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun KompotPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Backend-driven UI usually arrives as a sealed hierarchy of every widget the product happens to have: a client that must be released whenever the server learns a new component, and a serialiser that turns an unfamiliar type into a crash. kompot inverts that. The core knows about a tree, actions, modifiers and design tokens — nothing about what a \"product card\" is — and everything concrete is a plug-in module. An unknown type degrades to a placeholder rather than taking the screen down, and the degradation is reported to a sink the deployment owns.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Open contracts")
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("KompotComponent")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("KompotAction")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("FieldValue")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ValidationRule")
            }
            org.jetbrains.compose.web.dom.Text(" are interfaces; the application assembles the concrete set.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("One annotation")
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("@KompotComponentMarker")
            }
            org.jetbrains.compose.web.dom.Text(", and KSP writes the polymorphic registration and the renderer entry.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Forms")
            }
            org.jetbrains.compose.web.dom.Text(" — validation, visibility and cross-field rules run client-side; a server-relevant change asks for a targeted patch, and a server-computed value lands in a bound read-only field rather than an editable one.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Server-driven theming")
            }
            org.jetbrains.compose.web.dom.Text(" over open string tokens, down to text colour.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Per-user live updates")
            }
            org.jetbrains.compose.web.dom.Text(" — a screen names its own channel.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Wizards as pure functions")
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("(session, transition, draft) -> session")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Compose renderers")
            }
            org.jetbrains.compose.web.dom.Text(" for the standard, form, wizard and image components, plus a Material3 design system with surface roles a brand overrides.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A screen editor")
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("kompot-studio")
            }
            org.jetbrains.compose.web.dom.Text(" opens a body beside the renderers that will draw it, in the consumer's own brand frame, with findings from the schema, the rules, the vocabulary and the render itself; see ")
            com.varabyte.kobweb.silk.components.navigation.Link("/blog/a-screen-editor-for-backend-driven-ui") {
                org.jetbrains.compose.web.dom.Text("the write-up")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }

val kompotVersion = "0.33.1.88"

dependencies {
    implementation(platform("io.github.youndie:kompot-bom:${'$'}kompotVersion"))
    implementation("io.github.youndie:kompot-core")
    implementation("io.github.youndie:kompot-standard")
    implementation("io.github.youndie:kompot-ktor")
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A version carries the CI run number, so any two publishes differ; the BOM makes a mixed pair of versions impossible to write down.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-wire-is-a-specification") }) {
        org.jetbrains.compose.web.dom.Text("The wire is a specification")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The JSON Schema of every protocol module is generated from the very ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SerialDescriptor")
        }
        org.jetbrains.compose.web.dom.Text("s the responses are encoded with, and both the schemas and the written specification travel inside the published artefact — the numbered rules resolve from the jar, by id. Two conformance kits hold the two sides: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kompot-tck")
        }
        org.jetbrains.compose.web.dom.Text(" walks a running server over HTTP with twelve checks, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kompot-client-tck")
        }
        org.jetbrains.compose.web.dom.Text(" is a corpus of eighteen cases any client answers through a seven-operation adapter, with a coverage report naming the rules nothing holds yet. Every release is read back from the repository by ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/proba") {
            org.jetbrains.compose.web.dom.Text("proba")
        }
        org.jetbrains.compose.web.dom.Text(", and the verdict badge is written by the run that published it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Your own components need your own renderers; the SwiftUI side is yours entirely.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The live-update transport (SSE or WebSocket) is the application's.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Business validation belongs to the server; the client highlights the field a refusal names.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The Compose half pairs Compose Multiplatform 1.11.1 with material3 ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("1.11.0-alpha07")
            }
            org.jetbrains.compose.web.dom.Text("; mixing Compose lines fails at runtime, not at resolution.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("iosX64")
            }
            org.jetbrains.compose.web.dom.Text(" is unreachable for the Compose renderers — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("compose.runtime")
            }
            org.jetbrains.compose.web.dom.Text(" stopped publishing it. Device and Apple-silicon simulator targets are covered; the protocol modules carry all three iOS targets.")
        }
    }
}
